import { useWorkspaceText } from './interface-copy';
// Processing and failure views of the meeting canvas. Owner: WP-5 (BUILD 1).
//
// The dispatcher renders MeetingProcessingView while `status === 'processing'`
// (except the `sealing` stage, which is WP-4's capture view) and
// MeetingFailedView for a failed meeting without a transcript. While the
// recording is transcribed the canvas is the finishing view (PL-30,
// meeting-finishing.tsx): the live transcript stays under a quiet progress
// header. The failure sentence names the stage that failed (meeting-status.ts);
// a ready meeting whose notes are still being written is not "processing"
// (S6, F7). Copy: ns/meetings-notes.ts. Props: meeting-slots.ts.
import { useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import meetingsNotesCopy from '@/i18n/ns/meetings-notes';
import { useWorkspace } from './context';
import { WorkspaceIcon } from './icons';
import { MeetingFinishingView } from './meeting-finishing';
import { meetingFailureKey } from './meeting-status';
import { meetingsApi } from './meetings-api';
import type { MeetingFailedViewProps, MeetingProcessingViewProps } from './meeting-slots';

export function MeetingProcessingView({ meeting, ...slots }: MeetingProcessingViewProps) {
  return <MeetingFinishingView meeting={meeting} {...slots} />;
}

export function MeetingFailedView({
  meeting,
  onRetry,
  memberNotes,
  navigation,
  panel,
}: MeetingFailedViewProps) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsNotesCopy);
  const [busy, setBusy] = useState(false);
  const retry = () => {
    setBusy(true);
    void meetingsApi
      .retry(meeting.id)
      .then(() => onRetry())
      .catch((failure) => ws.notify(failure instanceof Error ? failure.message : copy.retryFailed))
      .finally(() => setBusy(false));
  };
  // The sentence says what happened; the raw code is one click away (DESIGN-SPEC §6.13).
  return (
    <div className="ws-canvas-body ws-meeting-failed">
      <div className="ws-eyebrow">{copy.failedEyebrow}</div>
      <h1>{meeting.title}</h1>
      {navigation}
      <div {...panel}>
        <p data-stage={meeting.stage}>{copy[meetingFailureKey(meeting.stage)]}</p>
        {/* The company's usage credit ran out (#2841): Retry works once a card is added. */}
        {meeting.failureCode === 'meeting_payment_required' && <p>{copy.paymentRequired}</p>}
        <details className="ws-meeting-failure-details">
          <summary>
            <WorkspaceIcon name="chevron" />
            {copy.failureDetails}
          </summary>
          <code>{meeting.failureCode ?? uiText('meeting_processing_failed')}</code>
        </details>
        {meeting.canEdit !== false && meeting.canRetry !== false ? (
          <div className="ws-actions">
            <button className="ws-button primary" disabled={busy} onClick={retry}>
              {copy.retry}
            </button>
          </div>
        ) : (
          <p className="ws-page-note">{copy.retryOwnerOnly}</p>
        )}
        {memberNotes}
      </div>
    </div>
  );
}
